import { Component, OnInit } from '@angular/core';

import {
    AzavistaApiService, EventType, EventWorkflowParentType, EventWorkflowTemplateType, IEvent, IEventWaitingListWidgetDataResponse,
    IEventWorkflow
} from '@azavista/servicelib';
import { AbstractWidgetDirective, IWidgetComponentDefinition } from '@azavista/components/widgets';
import { SharedService, IWidgetDataChanges } from '../../../shared';
import { WidgetsUtilsService } from '../widgets-utils.service';
import { EventWaitingListWidgetService } from './event-waiting-list-widget.service';
import { IEventWaitingListSettings, IFieldsData } from './interfaces';
import { TranslationKey } from '../../enums';
import { CommonDialogsService } from '../../common-dialogs.service';
import { IObjectEnumFieldWithValue, IObjectWithId, IValueChangedEventArgs } from '@azavista/components/shared';

@Component({
    templateUrl: 'event-waiting-list-widget.component.html',
    styleUrls: ['event-waiting-list-widget.component.css'],
    standalone: false
})
export class EventWaitingListWidgetComponent extends AbstractWidgetDirective implements OnInit {
    definitionSettings: IEventWaitingListSettings;
    isEventPredefined = false;
    event: IEvent;
    eventNotAvailable = false;
    fieldsData: IFieldsData;
    responseData: IEventWaitingListWidgetDataResponse;

    private eventId = '';

    constructor(
        private apiSvc: AzavistaApiService,
        private eEventWaitingListWidgetSvc: EventWaitingListWidgetService,
        private sharedSvc: SharedService,
        private widgetsUtilsSvc: WidgetsUtilsService,
        private commonDlgsSvc: CommonDialogsService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.definitionSettings = this.definition.settings as IEventWaitingListSettings;
        this.adjustDefinitionSettings(this.definitionSettings);
        this.definition.data = null;
        this.eventId = this.definition.context?.eventId;

        try {
            if (this.eventId) {
                // The widget is inside event context
                await this.setEventFromContext(this.eventId);
                this.eventNotAvailable = false;
            } else {
                this.eventId = this.definition.settings.remote.event_id;
            }
        } catch (err) {
            this.eventNotAvailable = true;
            return;
        }

        this.fieldsData = await this.createFieldsData(this.eventId, this.definitionSettings.remote.workflow_id);

        super.subscribeToDataChanges({
            next: (newData?: IWidgetDataChanges) => {
                this.dataChanged(newData);
            }
        });

        if (this.configMode) {
            if (this.eventId) {
                // Initially add only the selected event as item in event autocomplete
                try {
                    this.event ||= await this.widgetsUtilsSvc.getEvent(this.eventId);
                    this.eventNotAvailable = false;
                } catch (err) {
                    this.eventNotAvailable = true;
                }
            }
            this.setDefaultRemoteSettingsIfMissing();
            this.onSettingsChanged();
        } else {
            const hasRemoteSettings = !this.widgetsUtilsSvc.areRemoteSettingsEmpty(this.definitionSettings.remote);
            if (hasRemoteSettings) {
                // First check if the event exists
                try {
                    this.event = await this.apiSvc.getEvent(this.eventId);
                    this.eventNotAvailable = false;
                    // Start loading on next loop because it causes loading output to be emitted which could cause
                    // Angular change detection errors in the consumer (the dashboard component) if it changes template bound variables
                    // in the event handler
                    window.setTimeout(() => this.loadWidgetData(this.definition));
                } catch {
                    this.eventNotAvailable = true;
                    return;
                }
            } else {
                // Notify parent that we need remote settings
                this.settingsNeeded.next();
            }
        }
    }

    private adjustDefinitionSettings(definitionSettings: IEventWaitingListSettings): void {
        definitionSettings.remote.sessions_free_in ||= 0;
        if (definitionSettings.remote.sessions_free_in < 0) {
            definitionSettings.remote.sessions_free_in = 0;
        }
    }

    private async createFieldsData(eventId?: string, selectedWorkflowId?: string): Promise<IFieldsData> {
        const fieldsData: IFieldsData = {
            workflow: await this.createWorkflowFieldData(eventId, selectedWorkflowId),
        };
        return fieldsData;
    }

    private async createWorkflowFieldData(eventId?: string, selectedWorkflowId?: string): Promise<IObjectEnumFieldWithValue | null> {
        if (!this.eventId) {
            return null;
        }
        const allWorkflowsRes = await this.apiSvc.getAllEventWorkflows(EventWorkflowParentType.event, eventId);
        const waitinglistWorkflowTypes = new Set<EventWorkflowTemplateType>([EventWorkflowTemplateType.paymentWaitingList, EventWorkflowTemplateType.registrationWaitingList]);
        const waitinglistWorkflows = allWorkflowsRes.workflows.filter(x => waitinglistWorkflowTypes.has(x.type));
        const workflowFieldData: IObjectEnumFieldWithValue = {
            field: this.sharedSvc.createDefaultObjectEnumField('workflow'),
            value: this.sharedSvc.createDefaultObjectEnumInputFieldValue(
                waitinglistWorkflows as IObjectWithId[], selectedWorkflowId, null
            )
        };
        return workflowFieldData;
    }

    onWorkflowChanged(args: IValueChangedEventArgs): void {
        const selectedWorkflowItem = args.value as IEventWorkflow;
        this.definitionSettings.remote.workflow_id = selectedWorkflowItem?.id;
        this.onSettingsChanged();
    }

    onFreeslotsValueChanged(seconds: number): void {
        this.definitionSettings.remote.sessions_free_in = seconds;
    }

    async setEventFromContext(contextEventId: string): Promise<void> {
        this.isEventPredefined = true;
        this.definitionSettings.remote.event_id = contextEventId;
        this.event = await this.widgetsUtilsSvc.getEvent(contextEventId);
    }

    async onSelectEvent(): Promise<void> {
        const title = this.sharedSvc.translate(TranslationKey.events);
        const dlgResult = await this.commonDlgsSvc.showSelectEventsDialog(title, true);
        if (!dlgResult || dlgResult.length === 0) {
            return;
        }
        this.event = dlgResult[0] as IEvent;
        const eventId = this.event ? this.event.id : null;
        this.definitionSettings.remote.event_id = eventId;
        this.eventId = eventId;
        this.definitionSettings.remote.workflow_id = undefined;
        this.fieldsData.workflow = null;
        this.fieldsData.workflow = await this.createWorkflowFieldData(this.eventId, this.definitionSettings.remote.workflow_id);
        this.onSettingsChanged();
    }

    setDefaultRemoteSettingsIfMissing(): void {
        if (!this.definitionSettings.remote) {
            this.definitionSettings.remote = { event_id: this.eventId, sessions_free_in: 0 };
        }
    }

    async loadWidgetData(definition: IWidgetComponentDefinition): Promise<void> {
        this.definitionSettings = definition.settings;
        const remoteSettings = this.definitionSettings.remote;
        this.eventNotAvailable = !remoteSettings.event_id;
        if (!remoteSettings.event_id) {
            return;
        }
        try {
            this.notifyLoading(true);
            const res = await this.apiSvc.getEventWaitingListWidgetData(
                remoteSettings.event_id, remoteSettings.sessions_free_in, remoteSettings.workflow_id,
            );
            this.responseData = res;
            definition.data = res;
            this.processDataLoaded(definition);
        } catch (err) {
            this.processDataLoadedError();
        } finally {
            this.notifyLoading(false);
        }
    }

    async processDataLoaded(definition: IWidgetComponentDefinition): Promise<void> {
        definition.dataChangedSubject.next({ data: definition.data });
    }

    processDataLoadedError(): void {
        // TODO: What if report data fails ?
        this.responseData = null;
    }

    dataChanged(newData?: IWidgetDataChanges): void {
        if (!this.configMode && newData.settings && newData.settings.remote) {
            // Not config mode and remote settings were changed - reload data from server
            this.loadWidgetData(this.definition);
        }
    }

    onSettingsChanged(): void {
        const settingsAreValid = this.eEventWaitingListWidgetSvc.areSettingsValid(this.definitionSettings);
        this.settingsChanged.next({
            definition: this.definition,
            settings: this.definitionSettings,
            settingsAreValid: settingsAreValid
        });
    }
}

